import { rowId } from "./autosave.ts";
/**
 * 改前 / 改后一格里放什么：对象和对象数组不再整段打 JSON（10-21 leo：一条奖励改了一张卡，
 * 两边各铺开三十多行 `"qty": 1000, "ref": null`，看不出改了哪儿）。
 *
 * - 数组按下标和另一头比，只留变了的那几项，其余记个数
 * - 奖励项（带 kind 的对象）一项一行，由界面写成「物品卡 · 今日穿搭 · 墨镜 × 2」
 * - 其他对象一行一个「字段：值」
 */

const canonical = (v: unknown): string => JSON.stringify(v ?? null, (_k, x) => x && typeof x === "object" && !Array.isArray(x)
  ? Object.fromEntries(Object.keys(x).sort().map((k) => [k, x[k]])) : x);

export interface ItemLike { kind: string; ref?: unknown; qty?: unknown }

/** 奖励项数组：每个元素都是带字符串 kind 的对象（空数组不算，交给通用写法显示「空」） */
export const isRewardItems = (v: unknown): v is ItemLike[] =>
  Array.isArray(v) && v.length > 0 && v.every((x) => !!x && typeof x === "object" && typeof (x as { kind?: unknown }).kind === "string" && Object.keys(x).every((k) => ["kind", "ref", "qty"].includes(k)));

/**
 * 数组的哪几项要显示：另一头也是数组时只留同一下标上不一样的（多出来的、少掉的都算变了）；
 * 另一头不是数组（整列从空变成有）就全显示
 */
export function changedElements(v: unknown[], other: unknown): { shown: Array<{ index: number; item: unknown; counterpart?: unknown }>; hidden: number } {
  const all = v.map((item, index) => ({ index, item }));
  if (!Array.isArray(other)) return { shown: all, hidden: 0 };
  const keyed = (xs: unknown[]) => xs.every((x) => rowId(x)) && new Set(xs.map(rowId)).size === xs.length;
  const byId = keyed(v) && keyed(other) ? new Map(other.map((x) => [rowId(x), x])) : null;
  const shown = all.map(({ index, item }) => ({ index, item, counterpart: byId ? byId.get(rowId(item)) : other[index] }))
    .filter(({ item, counterpart }) => canonical(item) !== canonical(counterpart));
  return { shown, hidden: v.length - shown.length };
}

/** 对象的字段：值为空的跳过（null / 空串 / 空数组），读起来像没填，列出来只是噪音 */
export const objectEntries = (v: object): Array<[string, unknown]> =>
  Object.entries(v).filter(([k, x]) => !HIDDEN_KEYS.has(k) && !(x == null || x === "" || (Array.isArray(x) && !x.length)
    || (typeof x === "object" && !Array.isArray(x) && !Object.keys(x).length)));
/** 对人没有意义的：内部 id（uuid，行名已经在「项目」列里）、建筑体系（整版只有一个，跟着版本走） */
const HIDDEN_KEYS = new Set(["id", "buildingSystem"]);

export const isImageUrl = (v: unknown): v is string => typeof v === "string" && /^(?:https?:\/\/\S+\.(?:png|jpe?g|webp|gif|svg)(?:[?#].*)?|data:image\/[^;]+;base64,.+)$/i.test(v);
const IMAGE_FIELDS = new Set(["img", "image", "imageUrl", "cover", "coverImg", "bgImg", "thumbImg", "icon", "skinUrl", "imgLocked", "imgUnlocked", "damaged"]);
/** Nested cards keep their picture as identifying context, even when only their translated name changed. */
export function imageValues(v: object): Array<[string, string]> {
  return Object.entries(v).filter((entry): entry is [string, string] => isImageUrl(entry[1]) || (IMAGE_FIELDS.has(entry[0]) && typeof entry[1] === "string" && /^https?:\/\/\S+$/.test(entry[1])));
}

/** Make edits to otherwise invisible whitespace distinguishable in before/after text. */
export function visibleText(v: string): string {
  if (!/^\s|\s$|[ \u00a0\u3000]{2,}|[\n\r\t]/.test(v)) return v;
  return JSON.stringify(v).replace(/ /g, "␠").replace(/\u00a0/g, "\\u00a0").replace(/\u3000/g, "\\u3000");
}
